import { useState, useEffect, useCallback } from 'react';
import { Filter, RefreshCw, X } from 'lucide-react';

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:3005') + '/api';

interface CohortRow {
  cohort_month: string;
  new_customers: number;
  day0_revenue: number;
  rev_30d: number;
  rev_90d: number;
  rev_180d: number;
  lifetime_rev: number;
  repeat_buyers: number;
  ad_spend: number;
}

interface SourceRow {
  source: string;
  contacts: number;
  paying: number;
  totalLtv: number;
  topCampaign: string | null;
}

interface PlatformCohortRow {
  cohort_month: string;
  platform_id: string;
  new_contacts: number;
  paying: number;
  cohort_ltv: number | null;
  avg_ltv_paying: number | null;
  rev_30d: number | null;
  rev_60d: number | null;
  rev_90d: number | null;
  rev_180d: number | null;
  rev_365d: number | null;
  latest_create_date: string | null;
  spend: number;
}

const PLATFORM_LABELS: Record<string, string> = {
  google: 'Google', bing: 'Microsoft', meta: 'Meta', youtube: 'YouTube', organic: 'Organic',
};
const PLATFORM_BADGE: Record<string, string> = {
  google: 'bg-amber-100 text-amber-800',
  bing: 'bg-blue-100 text-blue-800',
  meta: 'bg-violet-100 text-violet-800',
  youtube: 'bg-red-100 text-red-800',
  organic: 'bg-emerald-100 text-emerald-800',
};

const fmt = (v: number) => '$' + Math.round(Math.abs(Number(v) || 0)).toLocaleString();
const monthLabel = (d: string) => new Date(d + 'T00:00:00').toLocaleString('en', { month: 'short', year: '2-digit' });

/** Days since the cohort month ended — used to flag windows that are still open. */
const monthAge = (d: string) => {
  const end = new Date(d + 'T00:00:00');
  end.setMonth(end.getMonth() + 1);
  return Math.floor((Date.now() - end.getTime()) / 86_400_000);
};

const EXCLUDE_KEY = 'newCustomerCohorts.excludeClubs';

export default function NewCustomerCohorts() {
  const [since, setSince] = useState('2024-07-01');
  const [until, setUntil] = useState('');
  const [excludeClubs, setExcludeClubs] = useState(() => localStorage.getItem(EXCLUDE_KEY) === '1');
  const [rows, setRows] = useState<CohortRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<CohortRow | null>(null);
  const [sources, setSources] = useState<SourceRow[]>([]);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [platformRows, setPlatformRows] = useState<PlatformCohortRow[]>([]);
  const [platformLoading, setPlatformLoading] = useState(false);

  const toggleExclude = () => {
    setExcludeClubs(v => {
      try { localStorage.setItem(EXCLUDE_KEY, v ? '0' : '1'); } catch { /* private mode */ }
      return !v;
    });
  };

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/financial-reports/new-customer-cohorts?since=${since}${until ? `&until=${until}` : ''}${excludeClubs ? '&exclude=clubs' : ''}`);
      const j = await r.json();
      if (!j.success) throw new Error(j.error);
      setRows(j.data || []);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, [since, until, excludeClubs]);

  useEffect(() => { load(); }, [load]);

  const loadPlatforms = useCallback(async () => {
    setPlatformLoading(true);
    try {
      const r = await fetch(`${API_BASE}/financial-reports/new-customer-cohorts/source-performance?since=${since}${until ? `&until=${until}` : ''}`);
      const j = await r.json();
      if (j.success) setPlatformRows(j.data || []);
    } catch { /* section renders empty */ }
    setPlatformLoading(false);
  }, [since, until]);

  useEffect(() => { loadPlatforms(); }, [loadPlatforms]);

  const openSources = async (row: CohortRow) => {
    setSelected(row); setSources([]); setSourcesLoading(true);
    try {
      const month = String(row.cohort_month).slice(0, 10);
      const r = await fetch(`${API_BASE}/financial-reports/new-customer-cohorts/sources?month=${month}`);
      const j = await r.json();
      if (j.success) setSources(j.data || []);
    } catch { /* empty state */ }
    setSourcesLoading(false);
  };

  const tot = rows.reduce((a, r) => ({
    customers: a.customers + Number(r.new_customers),
    day0: a.day0 + Number(r.day0_revenue),
    d90: a.d90 + Number(r.rev_90d),
    lifetime: a.lifetime + Number(r.lifetime_rev),
    repeats: a.repeats + Number(r.repeat_buyers),
    spend: a.spend + Number(r.ad_spend),
  }), { customers: 0, day0: 0, d90: 0, lifetime: 0, repeats: 0, spend: 0 });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">New Customer Cohorts</h1>
          <p className="text-sm text-gray-500">
            Customers grouped by the month of their first paid order — acquisition cost, day-one spend, and how their value develops over 30 / 90 / 180 days.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input type="date" value={since} onChange={e => setSince(e.target.value)} className="border rounded px-2 py-1 text-sm" />
          <span className="text-gray-400">→</span>
          <input type="date" value={until} onChange={e => setUntil(e.target.value)} className="border rounded px-2 py-1 text-sm" />
          <button
            onClick={toggleExclude}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              excludeClubs ? 'bg-amber-100 text-amber-800 hover:bg-amber-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Filter className="h-4 w-4" /> {excludeClubs ? 'Excluding Clubs & Trade-Ins' : 'Exclude Clubs & Trade-Ins'}
          </button>
          <button onClick={load} className="flex items-center gap-1 bg-blue-600 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-700">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 text-red-700 p-3 rounded">{error}</div>}

      {excludeClubs && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2 text-sm">
          <span className="font-semibold">Filtered view</span> — Custom Club and Trade-In dollars are stripped from every order (line-level).
          Customers whose only purchases were clubs/trades are excluded, and club-first buyers re-cohort to their first non-club purchase month.
          Ad spend is unchanged, so CAC/ROAS reflect the non-club business only.
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-7 gap-4">
        {[
          ['New customers', tot.customers.toLocaleString()],
          ['Ad spend', fmt(tot.spend)],
          ['Blended CAC', tot.customers ? fmt(tot.spend / tot.customers) : '--'],
          ['Avg first day', tot.customers ? fmt(tot.day0 / tot.customers) : '--'],
          ['Avg 90-day value', tot.customers ? fmt(tot.d90 / tot.customers) : '--'],
          ['90d ROAS', tot.spend > 0 ? (tot.d90 / tot.spend).toFixed(1) + 'x' : '--'],
          ['Bought again', tot.customers ? Math.round(100 * tot.repeats / tot.customers) + '%' : '--'],
        ].map(([label, v]) => (
          <div key={label as string} className="bg-white rounded-lg shadow p-4">
            <div className="text-xs uppercase text-gray-500">{label}</div>
            <div className="text-xl font-bold text-gray-900">{v}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              {['Cohort', 'New customers', 'Ad spend', 'CAC', 'Day-0 revenue', '≤30d', '≤90d', '≤180d', 'Lifetime', '90d ROAS', 'Repeat %'].map(h => (
                <th key={h} className="px-3 py-2 text-right first:text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map(r => {
              const age = monthAge(String(r.cohort_month).slice(0, 10));
              const n = Number(r.new_customers) || 1;
              const spend = Number(r.ad_spend) || 0;
              return (
                <tr key={r.cohort_month} onClick={() => openSources(r)} className="cursor-pointer hover:bg-blue-50">
                  <td className="px-3 py-2 font-medium whitespace-nowrap">
                    {monthLabel(String(r.cohort_month).slice(0, 10))}
                    {age < 90 && <span className="ml-1.5 text-[10px] uppercase text-amber-600 bg-amber-50 rounded px-1 py-0.5">window open</span>}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold">{Number(r.new_customers).toLocaleString()}</td>
                  <td className="px-3 py-2 text-right">{spend > 0 ? fmt(spend) : <span className="text-gray-300">--</span>}</td>
                  <td className="px-3 py-2 text-right font-semibold text-blue-700">{spend > 0 ? fmt(spend / n) : <span className="text-gray-300">--</span>}</td>
                  <td className="px-3 py-2 text-right">{fmt(r.day0_revenue)}</td>
                  <td className="px-3 py-2 text-right text-gray-500">{fmt(r.rev_30d)}</td>
                  <td className="px-3 py-2 text-right">{fmt(r.rev_90d)}</td>
                  <td className="px-3 py-2 text-right text-gray-500">{fmt(r.rev_180d)}</td>
                  <td className="px-3 py-2 text-right font-semibold">{fmt(r.lifetime_rev)}</td>
                  <td className="px-3 py-2 text-right font-semibold text-emerald-700">
                    {spend > 0 ? (Number(r.rev_90d) / spend).toFixed(1) + 'x' : '--'}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-500">{Math.round(100 * Number(r.repeat_buyers) / n)}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ─── Cohort performance by Source (from the Wicked artifact) ─── */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-4 pt-4">
          <h2 className="text-lg font-bold text-gray-900">Cohort performance by Source</h2>
          <p className="text-sm text-gray-500">
            Contacts grouped by <span className="font-medium">create-date month × first-click platform</span> (leads + customers).
            Revenue is verified collected cash net of refunds — same basis as the Wicked cohort artifact — so totals differ slightly
            from the order-amount table above. Spend is per-platform for that month.
          </p>
        </div>
        <div className="overflow-x-auto mt-3">
          {platformLoading ? (
            <p className="text-sm text-gray-400 py-8 text-center">Loading…</p>
          ) : platformRows.length === 0 ? (
            <p className="text-sm text-gray-400 py-8 text-center">No platform cohort data for this range.</p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Cohort', 'Source', 'New contacts', 'Paying', 'Paying %', 'Spend', 'Cost / lead', 'nCAC', '30d Rev', '90d Rev', 'Cohort LTV', '30d ROAS', 'Cohort ROAS'].map(h => (
                    <th key={h} className="px-3 py-2 text-right first:text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {Array.from(new Set(platformRows.map(r => String(r.cohort_month).slice(0, 10))))
                  .sort()
                  .reverse()
                  .map(mo => {
                    const sub = platformRows
                      .filter(r => String(r.cohort_month).slice(0, 10) === mo)
                      .sort((a, b) => Number(b.new_contacts) - Number(a.new_contacts));
                    const t = sub.reduce((a, r) => ({
                      contacts: a.contacts + Number(r.new_contacts || 0),
                      paying: a.paying + Number(r.paying || 0),
                      ltv: a.ltv + Number(r.cohort_ltv || 0),
                      spend: a.spend + Number(r.spend || 0),
                      r30: a.r30 + Number(r.rev_30d || 0),
                      r90: a.r90 + Number(r.rev_90d || 0),
                    }), { contacts: 0, paying: 0, ltv: 0, spend: 0, r30: 0, r90: 0 });
                    const cell = (spendVal: number, ltvVal: number) =>
                      spendVal > 0
                        ? <span className={ltvVal / spendVal >= 1 ? 'text-emerald-700 font-semibold' : 'text-red-700 font-semibold'}>{(ltvVal / spendVal).toFixed(2)}x</span>
                        : <span className="text-gray-300">--</span>;
                    const platformTr = sub.map(r => {
                      const spend = Number(r.spend || 0);
                      const contacts = Number(r.new_contacts || 0);
                      const paying = Number(r.paying || 0);
                      return (
                        <tr key={`${mo}-${r.platform_id}`} className="hover:bg-gray-50">
                          <td className="px-3 py-1.5 whitespace-nowrap">{monthLabel(mo)}</td>
                          <td className="px-3 py-1.5 text-right">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[11px] font-semibold ${PLATFORM_BADGE[r.platform_id] || 'bg-gray-100 text-gray-600'}`}>
                              {PLATFORM_LABELS[r.platform_id] || r.platform_id}
                            </span>
                          </td>
                          <td className="px-3 py-1.5 text-right">{contacts.toLocaleString()}</td>
                          <td className="px-3 py-1.5 text-right">{paying.toLocaleString()}</td>
                          <td className="px-3 py-1.5 text-right text-gray-500">{contacts ? Math.round(100 * paying / contacts) + '%' : '--'}</td>
                          <td className="px-3 py-1.5 text-right">{spend > 0 ? fmt(spend) : <span className="text-gray-300">--</span>}</td>
                          <td className="px-3 py-1.5 text-right text-gray-500">{spend > 0 && contacts ? fmt(spend / contacts) : <span className="text-gray-300">--</span>}</td>
                          <td className="px-3 py-1.5 text-right font-semibold text-blue-700">{spend > 0 && paying ? fmt(spend / paying) : <span className="text-gray-300">--</span>}</td>
                          <td className="px-3 py-1.5 text-right text-gray-500">{fmt(r.rev_30d || 0)}</td>
                          <td className="px-3 py-1.5 text-right">{fmt(r.rev_90d || 0)}</td>
                          <td className="px-3 py-1.5 text-right font-semibold">{fmt(r.cohort_ltv || 0)}</td>
                          <td className="px-3 py-1.5 text-right">{cell(spend, Number(r.rev_30d || 0))}</td>
                          <td className="px-3 py-1.5 text-right">{cell(spend, Number(r.cohort_ltv || 0))}</td>
                        </tr>
                      );
                    });
                    return [
                      ...platformTr,
                      <tr key={`${mo}-blended`} className="bg-gray-50 font-semibold">
                        <td className="px-3 py-1.5 whitespace-nowrap">{monthLabel(mo)}</td>
                        <td className="px-3 py-1.5 text-right"><span className="inline-block px-1.5 py-0.5 rounded text-[11px] font-semibold bg-gray-900 text-white">Blended</span></td>
                        <td className="px-3 py-1.5 text-right">{t.contacts.toLocaleString()}</td>
                        <td className="px-3 py-1.5 text-right">{t.paying.toLocaleString()}</td>
                        <td className="px-3 py-1.5 text-right">{t.contacts ? Math.round(100 * t.paying / t.contacts) + '%' : '--'}</td>
                        <td className="px-3 py-1.5 text-right">{t.spend > 0 ? fmt(t.spend) : '--'}</td>
                        <td className="px-3 py-1.5 text-right">{t.spend > 0 && t.contacts ? fmt(t.spend / t.contacts) : '--'}</td>
                        <td className="px-3 py-1.5 text-right text-blue-700">{t.spend > 0 && t.paying ? fmt(t.spend / t.paying) : '--'}</td>
                        <td className="px-3 py-1.5 text-right">{fmt(t.r30)}</td>
                        <td className="px-3 py-1.5 text-right">{fmt(t.r90)}</td>
                        <td className="px-3 py-1.5 text-right">{fmt(t.ltv)}</td>
                        <td className="px-3 py-1.5 text-right">{cell(t.spend, t.r30)}</td>
                        <td className="px-3 py-1.5 text-right">{cell(t.spend, t.ltv)}</td>
                      </tr>,
                    ];
                  })}
              </tbody>
            </table>
          )}
        </div>
        <p className="text-xs text-gray-400 px-4 py-3">
          Source attribution = Wicked first-click utm_source (YouTube matched before Google so YT traffic isn't bucketed under Google;
          untracked = Organic). nCAC = platform spend ÷ paying customers. Cohorts younger than a horizon are still maturing. Refreshes hourly.
        </p>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between p-4 border-b">
              <div>
                <h2 className="text-lg font-bold text-gray-900">{monthLabel(String(selected.cohort_month).slice(0, 10))} — acquisition sources</h2>
                <p className="text-sm text-gray-500">First-click attribution for contacts acquired this month (Wicked)</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="overflow-y-auto p-4">
              {sourcesLoading ? (
                <p className="text-sm text-gray-400 py-8 text-center">Loading…</p>
              ) : sources.length === 0 ? (
                <p className="text-sm text-gray-400 py-8 text-center">No attributed contacts found.</p>
              ) : (
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-500 uppercase border-b">
                      <th className="text-left py-2 pr-2">Source</th>
                      <th className="text-right py-2 px-2">Contacts</th>
                      <th className="text-right py-2 px-2">Paying</th>
                      <th className="text-right py-2 px-2">Total LTV</th>
                      <th className="text-left py-2 pl-2">Top campaign</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sources.map(s => (
                      <tr key={s.source} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-1.5 pr-2 font-medium">{s.source}</td>
                        <td className="text-right py-1.5 px-2">{s.contacts.toLocaleString()}</td>
                        <td className="text-right py-1.5 px-2 text-gray-500">{s.paying.toLocaleString()}</td>
                        <td className="text-right py-1.5 px-2">{fmt(s.totalLtv)}</td>
                        <td className="py-1.5 pl-2 text-gray-500 truncate max-w-[260px]">{s.topCampaign || '--'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <p className="text-xs text-gray-400 px-4 pb-3">
              Source mix uses first-click contact attribution, so counts won't exactly match the first-paid-order cohort above (a contact can click in one month and first buy in another).
            </p>
          </div>
        </div>
      )}

      <p className="text-xs text-gray-400">
        Cohort = month of a customer's first paid order in the Wicked warehouse (Keap + Shopify, canonical paid orders). Ad spend = all paid platforms that month;
        CAC is blended (spend ÷ all new customers, including organic). 90d ROAS = cohort's 90-day revenue ÷ that month's spend.
        Months tagged "window open" haven't finished their 90-day window. Click a row for the acquisition source mix. Data refreshes hourly.
      </p>
    </div>
  );
}
